<template>
  <v-row justify="center">
    <v-dialog v-model="open" width="640px">
      <v-card class="grid" v-if="userData">
        <!-- close -->
        <v-toolbar elevation="0" class="title">
          <span>{{ $t('message.pages.users.table.user') }}</span>
          <v-spacer />
          <v-toolbar-items>
            <v-btn icon @click="handleClose">
              <v-icon>mdi-close</v-icon>
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- steps -->
        <v-card-text class="my-0 py-0 px-8">
          <v-row no-gutters>
            <v-col cols="12">
              <v-row
                no-gutters
                align="center"
                justify="center"
                class="flex-column"
              >
                <div v-if="editing === '' || isEditingName" class="grid">
                  <div class="grid__avatar">
                    <template v-if="loading">
                      <v-skeleton-loader
                        width="150"
                        height="150"
                        type="image"
                      />
                    </template>
                    <template v-else>
                      <img
                        v-if="hasAvatar"
                        class="grid__avatar--img"
                        :alt="userData.name"
                        :src="userData.image_path"
                      />
                      <v-avatar v-else class="grid__avatar--text" size="100%">
                        <span>
                          {{ userNameInitials }}
                        </span>
                      </v-avatar>
                      <div class="grid__avatar--ctrl">
                        <label for="avatarImg">
                          <v-icon>mdi-camera</v-icon>
                        </label>
                        <v-divider v-if="hasAvatar" color="white" vertical />
                        <base-btn
                          v-if="hasAvatar"
                          type="tertiary"
                          icon="mdi-delete"
                          @click="deleteImage"
                        />
                      </div>
                    </template>
                  </div>
                </div>
                <!-- file -->
                <aside class="hidden">
                  <v-file-input
                    id="avatarImg"
                    accept="image/png, image/jpeg, image/jpg, image/gif"
                    @change="handleUpload"
                  />
                </aside>
                <span class="mb-1 userName">
                  {{ userData ? userData.email : '-' }}
                </span>
                <span class="mb-1 userEmail">
                  {{ userData ? userData.email : '-' }}
                </span>
                <v-row no-gutters class="ml-2">
                  <v-card
                    v-if="!openEdition"
                    color="#e6e8f8"
                    class="rounded-pill px-3 py-1 mr-2 mb-1"
                    flat
                  >
                    <span class="text--center font-weight-medium black--text">
                      <span
                        v-if="userData && userData.role_name"
                        class="userRole"
                      >
                        {{ userData.role_name.toUpperCase() }}
                      </span>
                    </span>
                  </v-card>
                  <v-select
                    v-else
                    v-model="selectedRoleId"
                    :items="roles"
                    item-text="name"
                    item-value="id"
                    class="mr-2"
                    style="max-width: 140px; max-height: 35px !important"
                    hide-details
                    dense
                    outlined
                  >
                  </v-select>
                  <v-icon
                    v-if="!openEdition"
                    small
                    style="cursor: pointer"
                    @click="handleOpenRoleEdition"
                  >
                    mdi-pencil
                  </v-icon>
                  <v-progress-circular
                    v-else-if="loadingEdition"
                    class="mt-2 ml-1"
                    :size="16"
                    width="2"
                    color="gray"
                    indeterminate
                  />
                  <v-row no-gutters v-else>
                    <v-icon style="cursor: pointer" @click="handleSaveEdition">
                      mdi-check
                    </v-icon>
                    <v-icon style="cursor: pointer" @click="handleCloseEdition">
                      mdi-close
                    </v-icon>
                  </v-row>
                </v-row>
              </v-row>
            </v-col>
            <v-row no-gutters v-if="isPending" class="mt-4 mb-8">
              <v-col cols="12" class="d-flex align-center justify-center">
                <v-card flat color="#FFECB3" class="pa-3" width="400px">
                  <v-icon color="#FF8F00">mdi-alert</v-icon>
                  <span>
                    {{ $t('message.pages.users.userEdit.pendingInvite') }}
                  </span>
                </v-card>
              </v-col>
            </v-row>
            <v-row no-gutters v-else>
              <v-col cols="12" class="mt-8">
                <v-row no-gutters>
                  <v-col cols="8">
                    <span class="rowTitle">{{
                      $t('message.pages.users.userEdit.lastAccess')
                    }}</span>
                  </v-col>
                  <v-col cols="4">
                    <v-row no-gutters justify="end">
                      <span>
                        {{ formatDate(userData.updated_at) }}
                      </span>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" class="my-2">
                <v-divider />
              </v-col>
              <v-col cols="12" class="">
                <v-row no-gutters>
                  <v-col cols="8">
                    <span class="rowTitle">{{
                      $t('message.pages.users.userEdit.userCreated')
                    }}</span>
                  </v-col>
                  <v-col cols="4">
                    <v-row no-gutters justify="end">
                      <span> {{ formatDate(userData.created_at) }}</span>
                    </v-row>
                  </v-col>
                </v-row>
              </v-col>
              <v-col cols="12" class="mt-2">
                <v-divider />
              </v-col>
            </v-row>
          </v-row>
        </v-card-text>
        <v-card-actions class="px-6 py-6 mt-8">
          <v-row v-if="!isEditing" no-gutters justify="center" align="center">
            <v-btn small text color="red" @click="handleDeleteUser">
              {{ $t('message.pages.users.userEdit.removeUser') }}
            </v-btn>
            <v-spacer />
            <v-btn small color="primary" outlined disabled>{{
              $t('message.pages.users.userEdit.resetPass')
            }}</v-btn>
          </v-row>
          <v-row v-else no-gutters justify="center" align="center">
            <v-spacer />
            <v-btn color="primary" class="ml-2 mr-2">
              {{ $t('message.actions.save') }}
            </v-btn>
          </v-row>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-row>
</template>

<script>
import subUsersManagement from '@/services/subUsersManagement'
import imagesAPI from '@/services/imagesApi.js'
import { mapState } from 'vuex'

export default {
  name: 'UserEdit',
  components: {
    BaseBtn: () => import('@/components/BaseBtn.vue'),
  },
  data: () => ({
    open: false,
    userData: null,
    selectedRoleId: '',
    roles: [],
    moreDetails: false,
    isEditing: false,
    isPending: false,
    openEdition: false,
    loadingEdition: false,
    userPhotoFile: null,
    previewImageUrl: '',
    loading: false,
    editing: '',
  }),
  computed: {
    ...mapState('user', {
      userInfo: (state) => state.userPlanInfo.user,
    }),
    hasAvatar() {
      return !!this.userData.image_id && !!this.userData.image_path
    },
    userNameInitials() {
      const { name, email } = this.userInfo ?? {}
      let initials = ''

      if (name) initials = name
      else if (email) initials = email

      if (initials) {
        return initials
          .match(/(^\S\S?|\s\S)?/g)
          .map((v) => v.trim())
          .join('')
          .match(/(^\S|\S$)?/g)
          .join('')
          .toLocaleUpperCase()
      }

      return initials
    },
  },
  mounted() {
    this.$root.$on('openUserEdit', (data) => {
      Object.assign(this.$data, this.initialState())
      this.open = true
      this.userData = data.user
      this.roles = data.roles
      this.selectedRoleId = data.user.role_id
    })
  },
  beforeDestroy() {
    this.$root.$off('openUserEdit')
  },
  methods: {
    async handleUpload(file) {
      this.userPhotoFile = file
      await this.saveImage()
    },
    async deleteImage() {
      if (this.userData.image_id) {
        this.loading = true
        await imagesAPI.deleteImage(this.userData.image_id)
        this.userData.image_path = null
        this.userData.image_id = null
        this.loading = false
      }
    },
    async saveImage() {
      if (this.userData.image_id) await this.deleteImage()
      const data = new FormData()
      data.append('file', this.userPhotoFile)
      var reader = new FileReader()
      reader.readAsDataURL(this.userPhotoFile)
      reader.onload = async () => {
        const options = [
          {
            filename: this.userPhotoFile.name,
            type: 'template',
          },
        ]
        const data = await fetch(reader.result)
        const blobData = await data.blob()
        let ret = await imagesAPI.handleUploadImage(blobData, options)
        this.userData.image_path = `${ret.cdn_base}/${ret.storage_path}`
        this.userData.image_id = ret.id
        this.handleUpdateUser()
        return ret
      }
    },
    getLocale() {
      if (this.$i18next.resolvedLanguage === 'en') return 'en-US'
      return 'pt-BR'
    },
    capitalizeFirstLetter(val) {
      return val.charAt(0).toUpperCase() + val.slice(1)
    },
    handleClose() {
      this.open = false
    },
    handleMoreDetails() {
      this.moreDetails = true
    },
    backToUserView() {
      this.isEditing = false
    },
    handleEditPermission() {
      this.isEditing = true
    },
    handleOpenRoleEdition() {
      this.openEdition = true
    },
    handleCloseEdition() {
      this.openEdition = false
    },
    handleDeleteUser() {
      let userArray = []
      userArray.push(this.userData)
      this.$root.$emit('removeUsersConfirm', userArray)
      this.handleClose()
    },
    async handleSaveEdition() {
      this.loadingEdition = true
      await this.handleUpdateUser()
      this.loadingEdition = false
      this.openEdition = false
    },
    async handleUpdateUser() {
      let updateObject = {
        role_id: this.selectedRoleId,
      }
      if (this.userData.image_id) {
        updateObject['image_id'] = this.userData.image_id
      }
      try {
        let ret = await subUsersManagement.updateUser(
          this.userData.id,
          updateObject
        )
        const userData = ret.data.response
        this.$root.$emit('updateUser', userData)
        this.userData = userData
      } catch (e) {
        console.log('e', e)
      }
    },
    formatDate(timestamp) {
      const date = new Date(timestamp)
      // Get day, month and year
      const day = date.getDate().toString().padStart(2, '0')
      const month = (date.getMonth() + 1).toString().padStart(2, '0')
      const year = date.getFullYear().toString()
      // Get hours and minutes
      const hours = date.getHours().toString().padStart(2, '0')
      const minutes = date.getMinutes().toString().padStart(2, '0')
      // Format the date string
      const formattedDate = `${day}/${month}/${year} às ${hours}h${minutes}`
      return formattedDate
    },
    initialState() {
      return {
        open: false,
        userData: null,
        selectedRoleId: '',
        roles: [],
        moreDetails: false,
        isEditing: false,
        isPending: false,
        openEdition: false,
        loadingEdition: false,
        userPhotoFile: null,
        previewImageUrl: '',
      }
    },
  },
}
</script>

<style lang="scss" scoped>
.title {
  font-weight: 500;
  font-size: 18px;
}
.userName {
  font-weight: 500;
  font-size: 20px;
  line-height: 120%;
}
.userEmail {
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
}
.userRole {
  font-weight: 500;
  font-size: 14px;
  line-height: 150%;
  color: var(--v-brand-darken2);
}
.rowTitle {
  font-weight: 500;
  font-size: 16px;
  line-height: 150%;
  color: #373b54;
}
.detailsText {
  font-weight: 400;
  font-size: 12px;
  color: var(--v-brand-darken1);
  cursor: pointer;
}
.roleTitle {
  font-weight: 500;
  font-size: 18px;
  line-height: 120%;
}
.roleSubtitle {
  font-weight: 400;
  font-size: 16px;
  line-height: 150%;
}
.roleCard {
  border: 1px solid #a0acff !important;
  border-radius: 4px;
}
.hidden {
  position: absolute;
  top: 0;
  left: 0;
  z-index: -1;

  width: 0.1px;
  height: 0.1px;
  overflow: hidden;
  opacity: 0;
}
.grid {
  display: grid;
  &__avatar {
    $size: 150px;
    position: relative;
    margin: 0 auto 30px;
    display: flex;
    width: $size;
    height: $size;
    border: 4px solid var(--v-neutral-lighten3);
    border-radius: 100%;
    align-items: center;
    justify-content: center;
    overflow: hidden;
    &--text {
      background-color: var(--v-brand-lighten1);

      font-size: 2rem;
      color: #fff;
    }
    &--ctrl {
      display: none;
      background: rgba(0, 0, 0, 0.8);
      &::v-deep {
        .v-divider {
          $size: 60%;
          margin: 20% 0;
          border-color: rgba(#fff, 0.4);
          max-height: $size;
          min-height: $size;
        }
        label {
          padding: 0.75rem;
          cursor: pointer;
        }
        .base-button {
          $color: rgba(#000, 0);

          background-color: $color !important;
          border-color: $color !important;
        }
        .mdi-camera {
          color: white;
        }
        .mdi-delete {
          color: #ffcdd2;
        }
      }
    }
    &:hover {
      [class*='--ctrl'] {
        position: absolute;
        top: 0;
        right: 0;

        display: flex;
        width: 100%;
        height: 100%;

        align-items: center;
        justify-content: center;
      }
    }
  }
}
.grid {
  &__options {
    color: var(--v-neutral-lighten2);
    .edit:hover {
      color: var(--v-neutral-lighten3);
    }
  }
  &__footer {
    .theme--dark.v-input input {
      color: var(--v-neutral-base) !important;
    }
  }
}

.bg-dark,
.theme--dark {
  .detailsText {
    color: #70a5f2;
  }

  .userRole {
    color: #8fb1e3;
  }
}
</style>
